/**
 * Fabric.js v7 patch: Effects panel rendering — layer blur, background blur
 * and fill opacity.
 *
 * Fabric has no blur for shapes or text (`filters.Blur` only works on
 * images) and no way to blur what is behind an object, so this patch draws
 * both with the canvas `ctx.filter`, from the one method every object goes
 * through when it is painted: `FabricObject.render`.
 *
 *   - layerBlur      — the object is drawn sharp into a scratch canvas the
 *                      size of its bounds, then copied back through
 *                      `blur()`. Working on a small canvas keeps the cost
 *                      tied to the object's size, not the whole ad.
 *   - backgroundBlur — before the object is drawn, the pixels already under
 *                      it are copied, blurred, cut to the object's
 *                      silhouette and pasted back. The object's own
 *                      (semi-transparent) fill then lands on top.
 *   - effectFillOpacity — lowers the alpha of the fill only (shapes, text and
 *                      frame rects), separate from `opacity`, which fades
 *                      the whole layer.
 *
 * Blur values are ad pixels. `ctx.filter` lengths ignore the canvas
 * transform, so they are scaled by the context's scale, which covers zoom,
 * retina and export multipliers alike.
 *
 * Objects with no effects take Fabric's original code path unchanged.
 */
import { FabricImage, FabricObject, FabricText, Group, Path, util } from 'fabric';

import type { DeviceBox } from '@/types/canvas/effects';
import type { DropShadow } from '@/types/layers/layer';
import { isOverlayActive } from '@/services/animation/animationState';

/** Room around the object for the blur to fade out, as a multiple of the radius. */
const BLUR_PADDING = 3;

/** Monotonic counter for unique SVG filter ids. */
let svgFilterUid = 0;

/**
 * Set while a silhouette is drawn, so the fill-opacity hook below leaves the
 * mask fully opaque.
 */
let drawingSilhouette = false;

/**
 * Scratch canvases
 *
 * Reused between frames. A blurred group draws its children while its own
 * scratch canvases are in use, so each nesting level gets its own pair.
 */
const scratchPool: HTMLCanvasElement[] = [];
let scratchDepth = 0;

/**
 * Hooks
 */

// 1. Object.render — every object, every canvas: blurs around the normal draw.
const renderObject = FabricObject.prototype.render;

FabricObject.prototype.render = function (
  this: FabricObject,
  ctx: CanvasRenderingContext2D
) {
  const shadows = enabledDropShadows(this);
  const layerBlur = blurOf(this.layerBlur);
  const backgroundBlur = blurOf(this.backgroundBlur);
  const hasEffects = shadows.length > 0 || layerBlur > 0 || backgroundBlur > 0;

  if (!hasEffects || isHitTestContext(this, ctx)) {
    renderObject.call(this, ctx);
    return;
  }
  if (this.isNotVisible()) return;
  if (this.canvas?.skipOffscreen && !this.group && !this.isOnScreen()) {
    return;
  }

  if (!isOverlayActive(this)) {
    for (let i = shadows.length - 1; i >= 0; i--) {
      drawDropShadow(this, ctx, shadows[i]!);
    }
  }

  if (backgroundBlur) drawBackgroundBlur(this, ctx, backgroundBlur);
  if (layerBlur) drawWithLayerBlur(this, ctx, layerBlur);
  else renderObject.call(this, ctx);
};

// 2. Object._renderFill — shape and frame fills: fill opacity.
const renderFill = FabricObject.prototype._renderFill;

FabricObject.prototype._renderFill = function (
  this: FabricObject,
  ctx: CanvasRenderingContext2D
) {
  const alpha = fillAlpha(this);
  if (alpha >= 1) {
    renderFill.call(this, ctx);
    return;
  }
  ctx.save();
  ctx.globalAlpha *= alpha;
  renderFill.call(this, ctx);
  ctx.restore();
};

// 3. Text._renderTextFill — text fills: fill opacity.
const renderTextFill = FabricText.prototype._renderTextFill;

FabricText.prototype._renderTextFill = function (
  this: FabricText,
  ctx: CanvasRenderingContext2D
) {
  const alpha = fillAlpha(this);
  if (alpha >= 1) {
    renderTextFill.call(this, ctx);
    return;
  }
  ctx.save();
  ctx.globalAlpha *= alpha;
  renderTextFill.call(this, ctx);
  ctx.restore();
};

// 4. toSVG — inject SVG filter definitions for custom drop shadows.
//    Path and FabricText override toSVG, so each needs its own wrapper.
function patchToSVG(proto: { toSVG: (reviver?: (svg: string) => string) => string }) {
  const original = proto.toSVG;
  proto.toSVG = function (this: FabricObject, reviver?: (svg: string) => string): string {
    const base = original.call(this, reviver);
    const shadows = enabledDropShadows(this);
    if (shadows.length === 0) return base;
    return wrapWithDropShadowFilters(this, base, shadows);
  };
}
patchToSVG(FabricObject.prototype);
patchToSVG(Path.prototype);
patchToSVG(FabricText.prototype);

/**
 * Helpers
 */

function withScratch<T>(
  width: number,
  height: number,
  draw: (canvas: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => T
): T | undefined {
  const canvas = (scratchPool[scratchDepth] ??= util.createCanvasElement());
  const ctx = canvas.getContext('2d');
  if (!ctx) return undefined;

  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  } else {
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.globalCompositeOperation = 'source-over';
    ctx.globalAlpha = 1;
    ctx.filter = 'none';
    ctx.clearRect(0, 0, width, height);
  }

  scratchDepth += 1;
  try {
    return draw(canvas, ctx);
  } finally {
    scratchDepth -= 1;
  }
}

/**
 * Geometry
 */

/** How many canvas pixels one ad pixel covers at this point of the render. */
function deviceScale(ctx: CanvasRenderingContext2D): number {
  const m = ctx.getTransform();
  return Math.hypot(m.a, m.b) || 1;
}

/** Point a scratch context at `box`, with the same transform as `ctx`. */
function alignScratch(
  scratch: CanvasRenderingContext2D,
  ctx: CanvasRenderingContext2D,
  box: DeviceBox
): void {
  const m = ctx.getTransform();
  scratch.setTransform(1, 0, 0, 1, -box.left, -box.top);
  scratch.transform(m.a, m.b, m.c, m.d, m.e, m.f);
}

/**
 * The canvas-pixel rectangle `obj` covers on `ctx`, grown by `padding` and
 * clipped to the canvas, or with `offCanvas` to `padding` beyond it. `null`
 * when nothing of it can reach the canvas. With `offCanvas`, `left` and `top`
 * may be negative; callers already offset by them.
 */
function deviceBounds(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D,
  padding: number,
  offCanvas = false
): DeviceBox | null {
  ctx.save();
  obj.transform(ctx);
  const m = ctx.getTransform();
  ctx.restore();

  const size = obj._getNonTransformedDimensions();
  const halfW = size.x / 2;
  const halfH = size.y / 2;
  const xs: number[] = [];
  const ys: number[] = [];
  for (const [x, y] of [
    [-halfW, -halfH],
    [halfW, -halfH],
    [halfW, halfH],
    [-halfW, halfH]
  ] as const) {
    xs.push(m.a * x + m.c * y + m.e);
    ys.push(m.b * x + m.d * y + m.f);
  }

  const shadow = obj.shadow;
  const shadowReach = shadow
    ? (Math.abs(shadow.offsetX) + Math.abs(shadow.offsetY) + shadow.blur) *
      deviceScale(ctx)
    : 0;
  const pad = padding + shadowReach;

  // With `offCanvas`, allow `pad` past the canvas rather than stopping at it: an
  // object running off an edge still casts into view, and a blur next to that
  // edge has to read the pixels beyond it or it fades out as if the object
  // ended there. Nothing further out than `pad` can reach a visible pixel, so
  // the box stays bounded. Only callers that *draw* the object may ask for it;
  // background blur reads the canvas itself, where there is nothing outside to
  // read and a negative source rect would be at the mercy of how the backend
  // clips `drawImage`.
  const slack = offCanvas ? Math.ceil(pad) : 0;
  const left = Math.max(-slack, Math.floor(Math.min(...xs) - pad));
  const top = Math.max(-slack, Math.floor(Math.min(...ys) - pad));
  const right = Math.min(ctx.canvas.width + slack, Math.ceil(Math.max(...xs) + pad));
  const bottom = Math.min(ctx.canvas.height + slack, Math.ceil(Math.max(...ys) + pad));
  if (right <= left || bottom <= top) return null;

  return { left, top, width: right - left, height: bottom - top };
}

/**
 * Silhouettes
 *
 * The shape background blur is cut to, drawn in solid black. Drop shadows
 * do not use these; see `drawShadowShape` below. Frames use
 * their clip rect, images their (rounded) box, groups the union of their
 * children; shapes and text use their own outline.
 */

/** Draw `obj`'s silhouette; `ctx` holds the parent's transform, as in `render`. */
function drawSilhouette(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D
): void {
  ctx.save();
  obj.transform(ctx);
  drawLocalSilhouette(obj, ctx);
  ctx.restore();
}

/** Draw `obj`'s silhouette in its own coordinate space. */
function drawLocalSilhouette(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D
): void {
  const clip = obj.clipPath;
  if (
    clip &&
    !clip.absolutePositioned &&
    !clip.inverted &&
    (obj instanceof Group || obj instanceof FabricImage)
  ) {
    ctx.save();
    ctx.transform(...clip.calcOwnMatrix());
    // Clip paths are typed as Fabric's base class; they render the same way.
    paintSolid(clip as FabricObject, ctx);
    ctx.restore();
    return;
  }

  if (obj instanceof Group) {
    for (const child of obj.getObjects()) {
      if (child.isNotVisible()) continue;
      ctx.save();
      ctx.transform(...child.calcOwnMatrix());
      drawLocalSilhouette(child, ctx);
      ctx.restore();
    }
    return;
  }

  if (obj instanceof FabricImage) {
    ctx.fillStyle = '#000';
    ctx.fillRect(-obj.width / 2, -obj.height / 2, obj.width, obj.height);
    return;
  }

  paintSolid(obj, ctx);
}

/**
 * Run the object's own drawing with fill forced to black and no stroke or
 * shadow, then put its styling back. Synchronous, so nothing can observe
 * the swapped values.
 */
function paintSolid(obj: FabricObject, ctx: CanvasRenderingContext2D): void {
  const { fill, stroke, shadow } = obj;
  obj.fill = '#000';
  obj.stroke = null;
  obj.shadow = null;
  drawingSilhouette = true;
  try {
    obj._render(ctx);
  } finally {
    obj.fill = fill;
    obj.stroke = stroke;
    obj.shadow = shadow;
    drawingSilhouette = false;
  }
}

/**
 * Shadow shapes
 *
 * What a drop shadow is cut to. Unlike a silhouette, this is whatever the
 * object really paints, alpha, fill and stroke alike.
 */

/**
 * Draw what `obj` actually paints, as the shape its drop shadow takes.
 *
 * `drawLocalSilhouette` stands a solid box in for an image and drops the
 * stroke, which is what background blur wants and a shadow does not: a cutout
 * PNG would cast its box rather than its subject, a stroke-only shape a filled
 * shadow, and a `Line` none at all. Colour does not matter here - the caller
 * floods the result with the shadow's colour through `source-in`, which keeps
 * only alpha.
 */
function drawShadowShape(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D
): void {
  if (obj instanceof Group) {
    for (const child of obj.getObjects()) {
      if (child.isNotVisible()) continue;
      ctx.save();
      ctx.transform(...child.calcOwnMatrix());
      drawShadowShape(child, ctx);
      ctx.restore();
    }
    return;
  }

  // Fabric's own shadow belongs to the object, not to the shape it casts.
  const { shadow } = obj;
  obj.shadow = null;
  // Hold the fill-opacity hooks at full alpha, as they are for a silhouette.
  drawingSilhouette = true;
  ctx.save();
  try {
    // Full alpha: the stamp below applies intensity and the object's opacity.
    ctx.globalAlpha = 1;
    obj._render(ctx);
  } finally {
    ctx.restore();
    obj.shadow = shadow;
    drawingSilhouette = false;
  }
}

/**
 * Cut the shape just drawn on `ctx` back to `obj`'s clip path.
 *
 * `_render` leaves clip paths to `render`, so a cropped image or frame would
 * otherwise cast a shadow reaching past its crop. Called once, on the finished
 * shape: the same `destination-in` inside the group recursion above would
 * erase the children drawn before it.
 */
function clipShadowShape(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D
): void {
  const clip = obj.clipPath;
  if (!clip || clip.absolutePositioned || clip.inverted) return;

  ctx.save();
  ctx.globalCompositeOperation = 'destination-in';
  ctx.transform(...clip.calcOwnMatrix());
  // Clip paths are typed as Fabric's base class; they render the same way.
  paintSolid(clip as FabricObject, ctx);
  ctx.restore();
}

/**
 * Effects
 */

/**
 * Replace the backdrop inside `obj`'s silhouette with a blurred copy of it.
 *
 * The blurred copy must replace the sharp pixels, not sit on top of them:
 * ad canvases are transparent where nothing is drawn, so a blurred shape
 * fades to see-through at its edges and the sharp original would show
 * through. Inside the silhouette the result is
 * `sharp × (1 − opacity) + blurred × opacity`.
 */
function drawBackgroundBlur(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D,
  blur: number
): void {
  const radius = blur * deviceScale(ctx);
  const box = deviceBounds(obj, ctx, radius * BLUR_PADDING);
  if (!box) return;

  withScratch(box.width, box.height, (blurred, blurredCtx) => {
    // 1. Copy what is already painted under the object, blurred.
    blurredCtx.filter = `blur(${radius}px)`;
    blurredCtx.drawImage(
      ctx.canvas,
      box.left,
      box.top,
      box.width,
      box.height,
      0,
      0,
      box.width,
      box.height
    );
    blurredCtx.filter = 'none';

    withScratch(box.width, box.height, (mask, maskCtx) => {
      // 2. Keep only the blurred pixels inside the object's silhouette.
      alignScratch(maskCtx, ctx, box);
      drawSilhouette(obj, maskCtx);
      blurredCtx.globalCompositeOperation = 'destination-in';
      blurredCtx.drawImage(mask, 0, 0);

      // 3. Swap sharp for blurred inside the silhouette, by the object's
      //    (and its parents') opacity: erase that share of the sharp
      //    pixels, then add the same share of the blurred ones.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha *= obj.opacity;
      ctx.globalCompositeOperation = 'destination-out';
      ctx.drawImage(mask, box.left, box.top);
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(blurred, box.left, box.top);
      ctx.restore();
    });
  });
}

/** Draw `obj` through a blur, on scratch canvases sized to its bounds. */
function drawWithLayerBlur(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D,
  blur: number
): void {
  const radius = blur * deviceScale(ctx);
  // Draws the object, so it needs the part of it that runs off the canvas.
  const box = deviceBounds(obj, ctx, radius * BLUR_PADDING, true);
  if (!box) return;

  withScratch(box.width, box.height, (sharp, sharpCtx) => {
    // 1. Fabric draws the object as usual (fill, stroke, opacity, shadow).
    alignScratch(sharpCtx, ctx, box);
    sharpCtx.globalAlpha = ctx.globalAlpha;
    renderObject.call(obj, sharpCtx);

    // 2. Blur the whole drawing at once, so a group blurs as one picture.
    withScratch(box.width, box.height, (blurred, blurredCtx) => {
      blurredCtx.filter = `blur(${radius}px)`;
      blurredCtx.drawImage(sharp, 0, 0);

      // 3. Paste it back in raw canvas pixels.
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = obj.globalCompositeOperation;
      ctx.drawImage(blurred, box.left, box.top);
      ctx.restore();
    });
  });
}

/**
 * Drop shadows
 */

/** Enabled shadows with at least some visual effect. */
function enabledDropShadows(obj: FabricObject): DropShadow[] {
  const list = obj.dropShadows;
  if (!list || list.length === 0) return [];
  return list.filter(
    s =>
      s.enabled &&
      s.intensity > 0 &&
      (s.blur > 0 || s.spread !== 0 || s.offsetX !== 0 || s.offsetY !== 0)
  );
}

/**
 * Render a single drop shadow behind `obj`.
 *
 * Pipeline:  silhouette → spread-scale → blur → offset + composite
 *
 * 1. Draw what the object really paints, keeping only its alpha.
 * 2. Scale it by `spread` (percentage) around the object's centre.
 * 3. Blur the result by `blur` pixels.
 * 4. Stamp it onto the main canvas at the shadow's (offsetX, offsetY)
 *    with `intensity` as opacity.
 */
function drawDropShadow(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D,
  shadow: DropShadow
): void {
  const pxScale = deviceScale(ctx);
  const blurPx = shadow.blur * pxScale;
  const offsetPx = { x: shadow.offsetX * pxScale, y: shadow.offsetY * pxScale };
  const spreadScale = 1 + shadow.spread / 100;
  if (spreadScale <= 0) return;

  // Spread is a share of the object itself, so it needs the object's size as
  // drawn: `_getNonTransformedDimensions` leaves out scaleX/scaleY, so an image
  // stored at its natural size and scaled down asks for padding many times the
  // room it uses, and a shape scaled up has its spread cut off square. Half the
  // size, because spread grows the shape about its centre; negative spread
  // pulls it inwards and needs no room.
  const objSize = obj._getTransformedDimensions();
  const spreadExtra = (Math.max(objSize.x, objSize.y) / 2)
    * (Math.max(0, shadow.spread) / 100) * pxScale;
  const padding =
    blurPx * BLUR_PADDING + spreadExtra
    + Math.max(Math.abs(offsetPx.x), Math.abs(offsetPx.y));
  const box = deviceBounds(obj, ctx, padding, true);
  if (!box) return;

  withScratch(box.width, box.height, (silhouetteCanvas, silhouetteCtx) => {
    // 1. Draw the shape the shadow takes: what the object really paints
    alignScratch(silhouetteCtx, ctx, box);
    silhouetteCtx.save();
    obj.transform(silhouetteCtx);
    if (spreadScale !== 1) silhouetteCtx.scale(spreadScale, spreadScale);
    drawShadowShape(obj, silhouetteCtx);
    clipShadowShape(obj, silhouetteCtx);
    silhouetteCtx.restore();

    // 2. Recolor: replace black with shadow color, keeping alpha
    silhouetteCtx.save();
    silhouetteCtx.setTransform(1, 0, 0, 1, 0, 0);
    silhouetteCtx.globalCompositeOperation = 'source-in';
    silhouetteCtx.fillStyle = shadow.color;
    silhouetteCtx.fillRect(0, 0, box!.width, box!.height);
    silhouetteCtx.restore();

    // 3. Blur if needed, then composite onto main canvas
    const stampShadow = (src: HTMLCanvasElement) => {
      ctx.save();
      const inheritedAlpha = ctx.globalAlpha;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.globalAlpha = (shadow.intensity / 100) * inheritedAlpha * obj.opacity;
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(src, box!.left + offsetPx.x, box!.top + offsetPx.y);
      ctx.restore();
    };

    if (blurPx > 0) {
      withScratch(box.width, box.height, (blurredCanvas, blurredCtx) => {
        blurredCtx.filter = `blur(${blurPx}px)`;
        blurredCtx.drawImage(silhouetteCanvas, 0, 0);
        stampShadow(blurredCanvas);
      });
    } else {
      stampShadow(silhouetteCanvas);
    }
  });
}

/**
 * SVG drop shadow export
 */

function wrapWithDropShadowFilters(
  obj: FabricObject,
  baseSvg: string,
  shadows: DropShadow[]
): string {
  const defs: string[] = [];
  let wrapped = baseSvg;

  for (const s of shadows.toReversed()) {
    const filterId = `drop_shadow_${++svgFilterUid}`;
    defs.push(buildDropShadowSvgFilter(obj, s, filterId));
    wrapped = `<g filter="url(#${filterId})">${wrapped}</g>`;
  }

  return '<defs>\n' + defs.join('\n') + '\n</defs>\n' + wrapped;
}

function buildDropShadowSvgFilter(
  obj: FabricObject,
  shadow: DropShadow,
  filterId: string
): string {
  const color = shadow.color;
  const intensity = shadow.intensity / 100;
  const stdDev = shadow.blur > 0 ? shadow.blur / 2 : 0;
  const spreadScale = 1 + shadow.spread / 100;

  const objSize = obj._getNonTransformedDimensions?.();
  const maxDim = objSize ? Math.max(objSize.x, objSize.y) : 100;
  const spreadExtra = maxDim * Math.abs(shadow.spread / 100);
  const reach = shadow.blur + spreadExtra +
    Math.abs(shadow.offsetX) + Math.abs(shadow.offsetY);
  const pad = obj.width && obj.height
    ? Math.ceil((reach / Math.min(obj.width, obj.height)) * 100) + 20
    : 40;

  const filterBox = `x="-${pad}%" y="-${pad}%" width="${100 + 2 * pad}%" height="${100 + 2 * pad}%"`;

  const lines = [
    `<filter id="${filterId}" ${filterBox}>`,
    `  <feFlood flood-color="${color}" flood-opacity="${intensity}" result="shadowColor"/>`,
    `  <feComposite in="shadowColor" in2="SourceAlpha" operator="in" result="shadowShape"/>`
  ];

  if (spreadScale !== 1 && spreadScale > 0) {
    lines.push(
      `  <feOffset in="shadowShape" result="preSpread"/>`,
      `  <feMorphology operator="${spreadScale > 1 ? 'dilate' : 'erode'}" radius="${Math.abs(shadow.spread / 2)}" in="preSpread" result="shadowShape"/>`
    );
  }

  if (stdDev > 0) {
    lines.push(
      `  <feGaussianBlur in="shadowShape" stdDeviation="${stdDev}" result="shadowBlur"/>`
    );
  } else {
    lines.push(`  <feOffset in="shadowShape" result="shadowBlur"/>`);
  }

  lines.push(
    `  <feOffset dx="${shadow.offsetX}" dy="${shadow.offsetY}" in="shadowBlur" result="shadowOffset"/>`,
    `  <feMerge>`,
    `    <feMergeNode in="shadowOffset"/>`,
    `    <feMergeNode in="SourceGraphic"/>`,
    `  </feMerge>`,
    `</filter>`
  );

  return lines.join('\n');
}

/** Positive blur radius, or 0 when the effect is off or set to 0. */
function blurOf(value: number | null | undefined): number {
  return typeof value === 'number' && value > 0 ? value : 0;
}

/**
 * Hit-testing re-renders an object into a tiny canvas on every hover;
 * effects would only slow that down and make blurred halos clickable.
 */
function isHitTestContext(
  obj: FabricObject,
  ctx: CanvasRenderingContext2D
): boolean {
  const canvas = obj.canvas as unknown as
    | { pixelFindContext?: CanvasRenderingContext2D }
    | undefined;
  return !!canvas && canvas.pixelFindContext === ctx;
}

/**
 * Fill opacity. A multi-part shape keeps it on its Group, so a path with no
 * value of its own reads its parent's.
 */
function fillAlpha(obj: FabricObject): number {
  if (drawingSilhouette) return 1;
  const value = obj.effectFillOpacity ?? obj.group?.effectFillOpacity;
  return typeof value === 'number' ? Math.min(1, Math.max(0, value)) : 1;
}
